import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '@/lib/result';
import { useSessionStore } from '@/stores/session.store';

import { ratingsRepository } from '../api/ratings.repository';
import type { RatingInput } from '../model/rating';

export const ratingKeys = {
  all: ['ratings'] as const,
  mine: (applicationId: string, raterId: string) =>
    [...ratingKeys.all, 'mine', applicationId, raterId] as const,
  canRateAsManager: (applicationId: string, userId: string) =>
    [...ratingKeys.all, 'canRate', applicationId, userId] as const,
};

/** Phase 6 (D-014, D-012): whether the signed-in user can submit the masjid-side
 * rating for this application. Backing RPC returns a boolean; used to gate the
 * form UI on the requester side of the RatingSection. */
export function useCanRateAsManager(applicationId: string, enabled: boolean) {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: ratingKeys.canRateAsManager(applicationId, userId ?? 'anon'),
    queryFn: () => unwrap(ratingsRepository.canRateAsManager(applicationId)),
    enabled: enabled && Boolean(userId),
  });
}

export function useMyRatingForApplication(applicationId: string) {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: ratingKeys.mine(applicationId, userId ?? 'anon'),
    queryFn: () =>
      unwrap(ratingsRepository.myRatingForApplication(applicationId, userId as string)),
    enabled: Boolean(userId),
  });
}

export function useCreateRating(applicationId: string) {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (input: RatingInput) => unwrap(ratingsRepository.create(input, userId as string)),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ratingKeys.mine(applicationId, userId ?? 'anon'),
      });
    },
  });
}
